import { useState } from "react";

import type { ReviewItem } from "../../api/types";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { t } from "../../i18n/t";
import { confirmTitleFor, labelForOption, requiresConfirm } from "../fxOpeningActions";

/**
 * FX opening-balance spec §3.7. Presentational: `DryRunScreen` owns `useReviewItems(id, {kind:
 * "FX_OPENING", mapping_version: "any"})` (mapping_version MUST be "any" -- FX_OPENING rows carry no
 * mapping version, and "current" -- the hook's own default -- hides every version-less row) and
 * `useResolveReviewItem`. Renders nothing when there are no FX_OPENING rows at all, open or resolved.
 *
 * `requiresConfirm`/`labelForOption`/`confirmTitleFor` moved to `../fxOpeningActions` (round-4 review
 * fix, review-fx-task2-r3.json, item 6) so the Scope screen's `FxOpeningBalances` can share them rather
 * than duplicate them a second time -- see that module's own docstring for the history.
 */

export function FxOpeningChecks({
  items,
  saving,
  baseCurrency,
  onAcknowledge,
}: {
  items: readonly ReviewItem[];
  saving: boolean;
  baseCurrency: string;
  onAcknowledge: (reviewItemId: string, option: string) => void;
}): JSX.Element | null {
  const [confirming, setConfirming] = useState<{
    reviewItemId: string;
    option: string;
    message: string;
  } | null>(null);

  if (items.length === 0) return null;
  const open = items.filter((item) => item.resolved_at === null);

  return (
    <section aria-labelledby="dryrun-fx-checks">
      <h2 id="dryrun-fx-checks">{t("fx.checks_title")}</h2>
      {open.length === 0 ? (
        <p>{t("fx.checks_empty")}</p>
      ) : (
        <ul>
          {open.map((item) => (
            <li key={item.id}>
              <Badge
                tone={item.severity === "BLOCK" ? "fail" : item.severity === "INFO" ? "neutral" : "warn"}
              >
                {t(`severity.${item.severity as "BLOCK" | "WARN" | "INFO"}` as "severity.BLOCK")}
              </Badge>{" "}
              {item.message}{" "}
              {item.options.length === 0 ? null : (
                <Button
                  aria-label={t("fx.action_for_finding", {
                    option: labelForOption(item.options[0] as string, baseCurrency),
                    message: item.message,
                  })}
                  disabled={saving}
                  onClick={() => {
                    const option = item.options[0] as string;
                    if (requiresConfirm(item.severity)) {
                      setConfirming({ reviewItemId: item.id, option, message: item.message });
                      return;
                    }
                    onAcknowledge(item.id, option);
                  }}
                >
                  {labelForOption(item.options[0] as string, baseCurrency)}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={confirming !== null}
        title={confirmTitleFor(confirming?.option ?? "")}
        onConfirm={() => {
          if (confirming !== null) onAcknowledge(confirming.reviewItemId, confirming.option);
          setConfirming(null);
        }}
        onCancel={() => setConfirming(null)}
      >
        <p>{confirming?.message}</p>
        {confirming?.option === "accept_estimate" ? <p>{t("fx.accept_estimate_confirm_body")}</p> : null}
      </ConfirmDialog>
    </section>
  );
}
